import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Phone, PhoneCall, PhoneOff, PhoneMissed, Voicemail,
  Mail, MessageSquare, Calendar, Clock, Tag, ArrowRight,
  Activity, BarChart3, Users, AlertTriangle, Plus, X, Search
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPE_CONFIG = {
  call: { icon: Phone, color: '#3B82F6', label: 'Call' },
  email: { icon: Mail, color: '#8B5CF6', label: 'Email' },
  sms: { icon: MessageSquare, color: '#22C55E', label: 'SMS' },
  meeting: { icon: Calendar, color: '#D4AF37', label: 'Meeting' },
  note: { icon: MessageSquare, color: '#6B7280', label: 'Note' },
  task: { icon: Clock, color: '#F59E0B', label: 'Task' },
  stage_change: { icon: ArrowRight, color: '#EC4899', label: 'Stage Change' },
  tag_change: { icon: Tag, color: '#06B6D4', label: 'Tag Change' },
  assignment: { icon: Users, color: '#10B981', label: 'Assignment' },
};

const CALL_OUTCOMES = [
  { id: 'answered', label: 'Answered', icon: PhoneCall, color: '#22C55E' },
  { id: 'voicemail', label: 'Voicemail', icon: Voicemail, color: '#F59E0B' },
  { id: 'no_answer', label: 'No Answer', icon: PhoneMissed, color: '#EF4444' },
  { id: 'busy', label: 'Busy', icon: PhoneOff, color: '#808080' },
];

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

// ============ CALL LOGGER MODAL ============
const CallLoggerModal = ({ onClose, onSuccess, preselectedLeadId = '' }) => {
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    lead_id: preselectedLeadId,
    activity_type: 'call',
    title: '',
    description: '',
    call_duration: 0,
    call_outcome: 'answered',
  });
  const [durationMins, setDurationMins] = useState(0);
  const [durationSecs, setDurationSecs] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/leads?limit=200`).then(res => {
      const data = res.data.leads || res.data || [];
      setLeads(data);
    }).catch(() => {});
  }, []);

  const filteredLeads = leads.filter(l =>
    !searchTerm || l.name?.toLowerCase().includes(searchTerm.toLowerCase()) || l.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!form.lead_id) { toast.error('Select a lead'); return; }
    if (!form.call_outcome) { toast.error('Select call outcome'); return; }
    setLoading(true);
    const totalSeconds = (parseInt(durationMins) || 0) * 60 + (parseInt(durationSecs) || 0);
    const selectedLead = leads.find(l => l.id === form.lead_id);
    try {
      await axios.post(`${API}/leads/${form.lead_id}/activities`, {
        ...form,
        title: form.title || `Call - ${CALL_OUTCOMES.find(o => o.id === form.call_outcome)?.label || form.call_outcome}`,
        call_duration: totalSeconds,
      });
      toast.success(`Call logged for ${selectedLead?.name || 'lead'}`);
      onSuccess();
      onClose();
    } catch {
      toast.error('Failed to log call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="call-logger-modal">
        <div className="p-5 border-b border-[#222] flex items-center justify-between">
          <h3 className="text-lg text-[#F5F5F0] font-medium" style={{ fontFamily: 'Playfair Display' }}>
            <PhoneCall className="w-5 h-5 inline mr-2 text-[#D4AF37]" />
            Log a Call
          </h3>
          <button onClick={onClose} className="text-[#808080] hover:text-[#F5F5F0]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Lead Selection */}
          <div>
            <label className="text-[#808080] text-xs uppercase tracking-wider mb-1.5 block font-medium">Lead *</label>
            {!preselectedLeadId && (
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="call-lead-search"
                />
              </div>
            )}
            <select
              value={form.lead_id}
              onChange={e => setForm({ ...form, lead_id: e.target.value })}
              className="w-full px-3 py-2.5 bg-[#1A1A1A] border border-[#333] rounded-lg text-[#F5F5F0] text-sm"
              data-testid="call-lead-select"
            >
              <option value="">Select a lead...</option>
              {filteredLeads.map(l => (
                <option key={l.id} value={l.id}>{l.name} — {l.email}</option>
              ))}
            </select>
          </div>

          {/* Call Outcome */}
          <div>
            <label className="text-[#808080] text-xs uppercase tracking-wider mb-1.5 block font-medium">Outcome *</label>
            <div className="grid grid-cols-4 gap-2">
              {CALL_OUTCOMES.map(outcome => (
                <button
                  key={outcome.id}
                  onClick={() => setForm({ ...form, call_outcome: outcome.id })}
                  className={`p-2.5 rounded-lg border text-xs font-medium flex flex-col items-center gap-1.5 transition-all ${
                    form.call_outcome === outcome.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'border-[#333] bg-[#1A1A1A] text-[#808080] hover:border-[#555]'
                  }`}
                  data-testid={`call-outcome-${outcome.id}`}
                >
                  <outcome.icon className="w-4 h-4" style={{ color: form.call_outcome === outcome.id ? outcome.color : undefined }} />
                  {outcome.label}
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-[#808080] text-xs uppercase tracking-wider mb-1.5 block font-medium">Duration</label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" max="120"
                  value={durationMins}
                  onChange={e => setDurationMins(e.target.value)}
                  className="w-16 bg-[#1A1A1A] border-[#333] text-[#F5F5F0] text-center"
                  data-testid="call-duration-mins"
                />
                <span className="text-[#808080] text-xs">min</span>
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" max="59"
                  value={durationSecs}
                  onChange={e => setDurationSecs(e.target.value)}
                  className="w-16 bg-[#1A1A1A] border-[#333] text-[#F5F5F0] text-center"
                  data-testid="call-duration-secs"
                />
                <span className="text-[#808080] text-xs">sec</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[#808080] text-xs uppercase tracking-wider mb-1.5 block font-medium">Notes</label>
            <Textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Call notes, next steps, key discussion points..."
              rows={3}
              className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] text-sm"
              data-testid="call-notes"
            />
          </div>
        </div>

        <div className="p-5 border-t border-[#222] flex gap-3">
          <Button onClick={onClose} variant="outline" className="btn-outline-gold flex-1">Cancel</Button>
          <Button onClick={handleSubmit} className="btn-gold flex-1" disabled={loading} data-testid="submit-call-log">
            {loading ? 'Logging...' : 'Log Call'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ STAT CARD ============
const StatCard = ({ icon: Icon, label, value, subtext, color = '#D4AF37' }) => (
  <div className="bg-[#111111] border border-[#222] rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#F5F5F0]">{value}</p>
        <p className="text-[#808080] text-xs">{label}</p>
      </div>
    </div>
    {subtext && <p className="text-[#808080] text-[11px] mt-1">{subtext}</p>}
  </div>
);

// ============ MAIN PAGE ============
export const AdminActivities = () => {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [totalActivities, setTotalActivities] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [showCallLogger, setShowCallLogger] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/activities/stats`);
      setStats(res.data);
    } catch (error) { console.error('Activity stats fetch error:', error); }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('activity_type', filterType);
      params.append('limit', PAGE_SIZE);
      params.append('skip', page * PAGE_SIZE);
      const res = await axios.get(`${API}/activities?${params.toString()}`);
      setActivities(res.data.activities || []);
      setTotalActivities(res.data.total || 0);
    } catch {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [filterType, page]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleCallLogged = () => {
    fetchStats();
    fetchActivities();
  };

  const totalPages = Math.ceil(totalActivities / PAGE_SIZE);

  return (
    <AdminLayout>
      <div data-testid="admin-activities-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Activity <span className="text-gold-gradient">&</span> Call Tracking
            </h1>
            <p className="text-[#808080] mt-1">Track all interactions with your leads</p>
          </div>
          <Button onClick={() => setShowCallLogger(true)} className="btn-gold" data-testid="log-call-btn">
            <PhoneCall className="w-4 h-4 mr-2" /> Log a Call
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={PhoneCall} label="Calls Today" value={stats.calls.today} subtext={`${stats.calls.this_week} this week`} color="#3B82F6" />
            <StatCard icon={Phone} label="Total Calls" value={stats.calls.total} subtext={`${stats.calls.this_month} this month`} color="#22C55E" />
            <StatCard icon={Activity} label="Activities Today" value={stats.activities.today} subtext={`${stats.activities.this_week} this week`} color="#D4AF37" />
            <StatCard icon={AlertTriangle} label="Need Follow-Up" value={stats.stale_leads} subtext="No contact in 7+ days" color="#EF4444" />
          </div>
        )}

        {/* Call Outcomes Breakdown */}
        {stats && stats.calls.total > 0 && (
          <div className="bg-[#111111] border border-[#222] rounded-xl p-5 mb-8" data-testid="call-outcomes-breakdown">
            <h3 className="text-[#808080] text-xs uppercase tracking-wider mb-4 font-medium">Call Outcomes Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {CALL_OUTCOMES.map(outcome => {
                const count = stats.calls.outcomes[outcome.id] || 0;
                const pct = stats.calls.total > 0 ? Math.round((count / stats.calls.total) * 100) : 0;
                return (
                  <div key={outcome.id} className="bg-[#0a0a0a] border border-[#222] rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${outcome.color}15` }}>
                      <outcome.icon className="w-4 h-4" style={{ color: outcome.color }} />
                    </div>
                    <div>
                      <p className="text-[#F5F5F0] text-sm font-semibold">{count}</p>
                      <p className="text-[#808080] text-[10px]">{outcome.label} ({pct}%)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Activity Type Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={() => { setFilterType(''); setPage(0); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${!filterType ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-[#1A1A1A] text-[#808080] border border-[#333] hover:border-[#555]'}`}
            data-testid="filter-all"
          >
            All
          </button>
          {Object.entries(ACTIVITY_TYPE_CONFIG).map(([type, config]) => (
            <button
              key={type}
              onClick={() => { setFilterType(type); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${filterType === type ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-[#1A1A1A] text-[#808080] border border-[#333] hover:border-[#555]'}`}
              data-testid={`filter-${type}`}
            >
              <config.icon className="w-3 h-3" />
              {config.label}
            </button>
          ))}
        </div>

        {/* Activity Feed */}
        <div className="bg-[#111111] border border-[#222] rounded-xl" data-testid="activity-feed">
          <div className="p-4 border-b border-[#222] flex items-center justify-between">
            <h3 className="text-[#F5F5F0] text-sm font-medium">
              Activity Feed {totalActivities > 0 && <span className="text-[#808080]">({totalActivities})</span>}
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-[#808080]">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No activities yet</p>
              <p className="text-xs mt-1">Log a call or interact with leads to see activity here</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1A1A1A]">
              {activities.map(act => {
                const config = ACTIVITY_TYPE_CONFIG[act.activity_type] || ACTIVITY_TYPE_CONFIG.note;
                const Icon = config.icon;
                return (
                  <div key={act.id} className="p-4 hover:bg-[#1A1A1A]/30 transition-colors" data-testid={`activity-row-${act.id}`}>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${config.color}15` }}>
                        <Icon className="w-4 h-4" style={{ color: config.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-[#F5F5F0] text-sm font-medium">{act.title}</p>
                            <p className="text-[#D4AF37] text-xs mt-0.5">
                              {act.lead_name || 'Unknown Lead'}
                              {act.lead_email && <span className="text-[#808080]"> — {act.lead_email}</span>}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-[#808080] text-[10px] block">
                              {new Date(act.created_at).toLocaleDateString()}
                            </span>
                            <span className="text-[#808080] text-[10px] block">
                              {new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        {act.description && <p className="text-[#808080] text-xs mt-1">{act.description}</p>}
                        {/* Call-specific info */}
                        {act.activity_type === 'call' && (
                          <div className="flex items-center gap-3 mt-2">
                            {act.call_outcome && (
                              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                act.call_outcome === 'answered' ? 'bg-green-500/20 text-green-400' :
                                act.call_outcome === 'voicemail' ? 'bg-yellow-500/20 text-yellow-400' :
                                act.call_outcome === 'no_answer' ? 'bg-red-500/20 text-red-400' :
                                'bg-[#333] text-[#808080]'
                              }`}>
                                {CALL_OUTCOMES.find(o => o.id === act.call_outcome)?.label || act.call_outcome}
                              </span>
                            )}
                            {act.call_duration > 0 && (
                              <span className="text-[#808080] text-[10px] flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatDuration(act.call_duration)}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Stage change info */}
                        {act.activity_type === 'stage_change' && act.old_value && act.new_value && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[#808080] text-[10px]">{act.old_value}</span>
                            <ArrowRight className="w-3 h-3 text-[#D4AF37]" />
                            <span className="text-[#D4AF37] text-[10px]">{act.new_value}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-[#222] flex items-center justify-between">
              <p className="text-[#808080] text-xs">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="btn-outline-gold text-xs"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  className="btn-outline-gold text-xs"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Call Logger Modal */}
        {showCallLogger && (
          <CallLoggerModal
            onClose={() => setShowCallLogger(false)}
            onSuccess={handleCallLogged}
          />
        )}
      </div>
    </AdminLayout>
  );
};
